import { useLDClient } from 'launchdarkly-react-client-sdk';
import React, { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { capitalize as _capitalize, compact as _compact } from 'lodash';

import type { IProductLinks } from '@sine/sine-application-frame';
import { useTeamSwitcher } from '@sine/sine-team-switcher';

import authService, { authHelpers } from '../authentication/auth.service';
import rxEventHub, { RxEvent } from '../services/events/rx';
import featureService from '../team/feature/feature.service';
import { USER_ACTIONS } from '../team/permission/constants/actions';
import teamSwitcherService from '../team/teamSwitcher.service';
import { BASE_PATH, ROUTE_ID, SIDEBAR_ROUTES } from './routeConfig';

interface RoutesContextType {
  routes: typeof SIDEBAR_ROUTES;
  products: IProductLinks;
  navBarPermissions: { invitations: boolean; messages: boolean; checkIn: boolean };
}

const defaultRoutesContext: RoutesContextType = {
  routes: [],
  products: {
    core: undefined,
    workflows: undefined,
    companies: undefined,
    admin: undefined,
  },
  navBarPermissions: { invitations: false, messages: false, checkIn: false },
};

const RoutesContext = createContext(defaultRoutesContext);

export function useLinks() {
  return useContext(RoutesContext);
}

export default function LinkProvider({ children }: PropsWithChildren<unknown>) {
  const [products, setProducts] = useState(defaultRoutesContext.products);
  const [navBarPermissions, setNavBarPermissions] = useState(defaultRoutesContext.navBarPermissions);
  const [routes, setRoutes] = useState(defaultRoutesContext.routes);
  const { teamList, isLoading } = useTeamSwitcher();
  const ldClient = useLDClient();

  const initialiseRoutes = async () => {
    const currentTeam = teamSwitcherService.getCurrentTeam();

    try {
      await authHelpers.waitForPermissionsDefer();

      const isLobbyAdmin =
        authHelpers.hasPermission(USER_ACTIONS.LOBBY_CREATE) && !authHelpers.hasPermission(USER_ACTIONS.SITE_CREATE);

      if (isLobbyAdmin) {
        const LOCATIONS_ROUTE = _capitalize(ROUTE_ID.LOCATIONS);
        const sitesRouteIndex = SIDEBAR_ROUTES.findIndex((route) => route.label === LOCATIONS_ROUTE);
        SIDEBAR_ROUTES[sitesRouteIndex].path = `${BASE_PATH}locations/lobbies`;
      }

      const permittedRoutes = SIDEBAR_ROUTES.filter(
        (route) => !route.permission || authHelpers.hasPermission(route.permission),
      );

      setNavBarPermissions({
        invitations:
          authHelpers.hasPermission(USER_ACTIONS.INVITATION_CREATE) ||
          authHelpers.hasPermission(USER_ACTIONS.H_PROFILE_INVITATION),
        messages: authHelpers.hasPermission(USER_ACTIONS.DISPLAY_MESSAGE),
        checkIn:
          authHelpers.hasPermission(USER_ACTIONS.SIGN_IN) || authHelpers.hasPermission(USER_ACTIONS.H_PROFILE_PASS),
      });

      setRoutes(
        permittedRoutes.length
          ? _compact(
              await Promise.all(
                permittedRoutes.map(async (route) => {
                  if (!route.featureFlagged && !route.launchDarklyFlag) {
                    return route;
                  }

                  const coreFlagEnabled =
                    route.featureFlagged && currentTeam && (await featureService.isFeatureEnabled(route.id));
                  const launchDarklyFlagEnabled = route.launchDarklyFlag && ldClient?.variation(route.launchDarklyFlag);
                  if (coreFlagEnabled || launchDarklyFlagEnabled) {
                    return route;
                  }
                }),
              ),
            )
          : [],
      );

      const isTeamAdmin = authHelpers.hasPermission(USER_ACTIONS.TEAM_ADMIN);

      const _products = { ...defaultRoutesContext.products };

      _products.core = window.env.PUBLIC_URL;
      _products.workflows = window.env.REACT_APP_SINE_WORKFLOWS_CLIENT;
      _products.companies = window.env.REACT_APP_SINE_COMPANIES_CLIENT;

      if (isTeamAdmin) {
        _products.admin = window.env.REACT_APP_SINE_ADMIN_CLIENT;
      }

      setProducts(_products);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    const userDetails = authService.getAuthState().userDetails;
    // We only need to initialise the routes for those users who are not part of the team switcher.
    if (userDetails && !isLoading && !teamList.length) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      authHelpers.checkAuthenticated();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      initialiseRoutes();
    }
  }, [teamList]);

  useEffect(() => {
    const teamChangedSub = rxEventHub.on(RxEvent.TEAM_CHANGED, () => {
      ldClient?.waitForInitialization().finally(() => {
        void initialiseRoutes();
      });
    });

    return () => {
      teamChangedSub.unsubscribe();
    };
  }, []);

  const context = useMemo<RoutesContextType>(
    () => ({ routes, products, navBarPermissions }) satisfies RoutesContextType,
    [routes, products, navBarPermissions],
  );

  return <RoutesContext.Provider value={context}>{children}</RoutesContext.Provider>;
}
